/**
 * BatchAccountSelectModal Component
 *
 * Modal dialog for batch transferring transactions to a different payment account.
 * Displays available payment accounts with their types and balances, allowing
 * users to transfer multiple transactions at once.
 *
 * @component
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * const [selected, setSelected] = useState(new Set(['tx1', 'tx2']));
 *
 * <BatchAccountSelectModal
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   selectedTransactions={selected}
 *   onClearSelection={() => setSelected(new Set())}
 * />
 * ```
 *
 * @remarks
 * - Loads payment accounts from usePaymentAccounts hook
 * - Displays account type badge (Checking, Savings, Investment, etc.)
 * - Shows current balance for each account
 * - Validates that an account is selected before transfer
 * - Automatically clears selection after successful transfer
 *
 * @see {@link usePaymentAccounts} for account data loading
 * @see {@link useBatchOperations.batchUpdatePaymentAccount} for the transfer logic
 */

import { useState } from 'react';
import { CreditCard } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePaymentAccounts } from '@/hooks/usePaymentAccounts';
import { useBatchOperations } from '@/hooks/useBatchOperations';
import { toast } from 'sonner';

/**
 * Props for BatchAccountSelectModal component
 *
 * @interface BatchAccountSelectModalProps
 * @property {boolean} open - Controls modal visibility
 * @property {function} onOpenChange - Callback when modal open state changes
 * @property {Set<string>} selectedTransactions - Set of transaction IDs to transfer
 * @property {function} onClearSelection - Callback to clear selection after transfer
 */
export interface BatchAccountSelectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTransactions: Set<string>;
  onClearSelection: () => void;
}

export function BatchAccountSelectModal({
  open,
  onOpenChange,
  selectedTransactions,
  onClearSelection,
}: BatchAccountSelectModalProps) {
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const { accounts: paymentAccounts, loading: isLoadingAccounts } = usePaymentAccounts();
  const { batchUpdatePaymentAccount, isUpdating } = useBatchOperations();

  const selectedIds = Array.from(selectedTransactions);
  const count = selectedIds.length;

  const handleConfirm = async () => {
    if (!selectedAccountId) {
      toast.error('Selecione uma conta de pagamento');
      return;
    }

    try {
      await batchUpdatePaymentAccount(selectedIds, selectedAccountId);
      onOpenChange(false);
      onClearSelection();

      // Reset form
      setSelectedAccountId('');
    } catch (error) {
      // Error handling is done by the hook
      console.error('Batch account update error:', error);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setSelectedAccountId('');
  };

  const selectedAccount = paymentAccounts?.find(acc => acc.id === selectedAccountId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Alterar conta em lote</DialogTitle>
          <DialogDescription>
            Transfira {count} {count === 1 ? 'transação' : 'transações'} para outra conta de pagamento.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="payment-account">
              Nova conta de pagamento <span className="text-destructive">*</span>
            </Label>
            <Select
              value={selectedAccountId}
              onValueChange={setSelectedAccountId}
              disabled={isUpdating || isLoadingAccounts}
            >
              <SelectTrigger id="payment-account">
                <SelectValue placeholder="Selecione uma conta" />
              </SelectTrigger>
              <SelectContent>
                {paymentAccounts.length === 0 ? (
                  <SelectItem value="none" disabled>
                    Nenhuma conta disponível
                  </SelectItem>
                ) : (
                  paymentAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.name} ({account.account_type})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedAccount && (
            <div className="rounded-lg bg-muted p-3 text-sm">
              <p className="text-muted-foreground">
                <CreditCard className="inline-block mr-1 h-4 w-4" />
                {count} {count === 1 ? 'transação será transferida' : 'transações serão transferidas'} para:
              </p>
              <p className="mt-1 font-medium">
                {selectedAccount.name}
              </p>
              <p className="text-xs text-muted-foreground">
                {selectedAccount.account_type}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isUpdating}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedAccountId || isUpdating}
          >
            {isUpdating ? 'Transferindo...' : 'Confirmar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
